/**
 * channelVisuals - Regras visuais de canal (ícone, cores e label)
 *
 * Centraliza o mesmo mapa de ícones/cores já usado na listagem de inboxes
 * (`InboxListRow` / `InboxCard`) para permitir reuso em outras telas
 * (ex.: modal de perda de negócio com conversas ativas).
 *
 * RELEASE:
 *   2026-08-08 v1.1.0 - Adicionados gradientes de acento e cores de "bloom" por canal.
 *   2026-08-07 v1.0.0 - Versão inicial (extração da regra existente).
 */
import { MessageCircle, Instagram, MessagesSquare, Send, Globe } from 'lucide-react';
import type { ChannelType } from '@/components/tenant/InboxCard';

// Configuração de ícones e labels por canal
export const channelConfig: Record<ChannelType, { icon: typeof MessageCircle; label: string }> = {
  whatsapp: { icon: MessageCircle, label: 'WhatsApp' },
  whatsapp_oficial: { icon: MessageCircle, label: 'WhatsApp Oficial' },
  instagram: { icon: Instagram, label: 'Instagram' },
  messenger: { icon: MessagesSquare, label: 'Messenger' },
  telegram: { icon: Send, label: 'Telegram' },
  webchat: { icon: Globe, label: 'WebChat' },
};

// Cores dos ícones por canal
export const channelIconColors: Record<ChannelType, string> = {
  whatsapp: 'text-green-600 dark:text-green-400',
  whatsapp_oficial: 'text-emerald-600 dark:text-emerald-400',
  instagram: 'text-pink-600 dark:text-pink-400',
  messenger: 'text-blue-600 dark:text-blue-400',
  telegram: 'text-sky-500 dark:text-sky-400',
  webchat: 'text-muted-foreground',
};

// Cores de fundo dos ícones por canal
export const channelBgColors: Record<ChannelType, string> = {
  whatsapp: 'bg-green-500/10',
  whatsapp_oficial: 'bg-emerald-500/10',
  instagram: 'bg-gradient-to-br from-pink-500/10 to-purple-500/10',
  messenger: 'bg-blue-500/10',
  telegram: 'bg-sky-400/10',
  webchat: 'bg-muted',
};

// Gradiente de acento por canal (barra superior de cartões, tiles de ícone)
export const channelAccentGradients: Record<ChannelType, string> = {
  whatsapp: 'from-green-500 to-emerald-400',
  whatsapp_oficial: 'from-emerald-600 to-teal-400',
  instagram: 'from-fuchsia-500 via-pink-500 to-amber-400',
  messenger: 'from-blue-600 to-sky-400',
  telegram: 'from-sky-500 to-cyan-300',
  webchat: 'from-violet-500 to-indigo-400',
};

// Cor do "bloom" decorativo (blur) por canal
export const channelBloomBg: Record<ChannelType, string> = {
  whatsapp: 'bg-green-500/25',
  whatsapp_oficial: 'bg-emerald-500/25',
  instagram: 'bg-pink-500/25',
  messenger: 'bg-blue-500/25',
  telegram: 'bg-sky-400/25',
  webchat: 'bg-violet-500/25',
};

/**
 * Retorna ícone, label e classes de cor do canal informado.
 * Faz fallback seguro para `whatsapp` quando o canal for desconhecido/nulo.
 */
export function getChannelVisual(channelType?: string | null) {
  const key = (channelType || 'whatsapp') as ChannelType;
  const config = channelConfig[key] || channelConfig.whatsapp;

  return {
    Icon: config.icon,
    label: config.label,
    iconColor: channelIconColors[key] || channelIconColors.whatsapp,
    bgColor: channelBgColors[key] || channelBgColors.whatsapp,
    accentGradient: channelAccentGradients[key] || channelAccentGradients.whatsapp,
    bloomBg: channelBloomBg[key] || channelBloomBg.whatsapp,
  };
}
